import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  DEFAULT_LANGUAGE,
  isSupportedLanguage,
  type LanguageCode,
} from './config';
import { detectLanguage } from './detect-language';
import { writeStoredLanguage } from './storage';

import en from './translations/en.json';
import es from './translations/es.json';
import fr from './translations/fr.json';
import de from './translations/de.json';
import pt from './translations/pt.json';

type TranslationTree = { [key: string]: string | TranslationTree };

const TRANSLATIONS: Record<LanguageCode, TranslationTree> = {
  en: en as TranslationTree,
  es: es as TranslationTree,
  fr: fr as TranslationTree,
  de: de as TranslationTree,
  pt: pt as TranslationTree,
};

/** Variables for `{name}`-style interpolation in a translation string. */
export type TranslationVars = Record<string, string | number>;

/** `t('common.upload')` or `t('greeting', { name: 'Sam' })`. */
export type TranslateFn = (key: string, vars?: TranslationVars) => string;

export interface I18nContextValue {
  lang: LanguageCode;
  setLanguage: (lang: LanguageCode) => void;
  t: TranslateFn;
}

const I18nContext = createContext<I18nContextValue | null>(null);

function resolvePath(tree: TranslationTree, key: string): string | undefined {
  const value = key
    .split('.')
    .reduce<string | TranslationTree | undefined>((acc, segment) => {
      if (acc && typeof acc === 'object') return acc[segment];
      return undefined;
    }, tree);
  return typeof value === 'string' ? value : undefined;
}

function interpolate(template: string, vars?: TranslationVars): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) =>
    name in vars ? String(vars[name]) : match,
  );
}

export interface I18nProviderProps {
  children: React.ReactNode;
  /** Override the initial language (e.g. from a known user preference). */
  initialLanguage?: LanguageCode;
}

export const I18nProvider: React.FC<I18nProviderProps> = ({ children, initialLanguage }) => {
  const [lang, setLang] = useState<LanguageCode>(
    () => initialLanguage ?? detectLanguage(),
  );

  const setLanguage = useCallback((next: LanguageCode) => {
    if (!isSupportedLanguage(next)) return;
    setLang(next);
    writeStoredLanguage(next);
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback<TranslateFn>(
    (key, vars) => {
      const active = resolvePath(TRANSLATIONS[lang], key);
      if (active !== undefined) return interpolate(active, vars);

      const fallback = resolvePath(TRANSLATIONS[DEFAULT_LANGUAGE], key);
      if (fallback !== undefined) return interpolate(fallback, vars);

      return key;
    },
    [lang],
  );

  const value = useMemo<I18nContextValue>(
    () => ({ lang, setLanguage, t }),
    [lang, setLanguage, t],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useTranslation(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error('useTranslation must be used within an <I18nProvider>');
  }
  return ctx;
}
